import React from 'react';

interface PredictiveInsight {
  trajectory_analysis: {
    trajectory: 'positive' | 'negative' | 'mixed' | 'stable' | 'insufficient_data';
    confidence: number;
    trend_direction: 'increasing' | 'decreasing' | 'stable';
    trend_magnitude: number;
    daily_scores: Record<string, {
      score: number;
      positive_count: number;
      negative_count: number;
      neutral_count: number;
      total_tags: number;
    }>;
    positive_tags: string[];
    negative_tags: string[];
    neutral_tags: string[];
  };
  forecast: {
    forecast_text: string;
    forecast_type: 'positive_trajectory' | 'risk_decline' | 'mixed_unstable' | 'stable';
    confidence: number;
    top_tags: [string, number][];
    recommendations: string[];
  };
  tag_frequency: Record<string, number>;
  analysis_period: string;
  generated_at: string;
}

interface ForecastBlockProps {
  predictiveData: PredictiveInsight | null;
  isLoading: boolean;
}

const ForecastBlock: React.FC<ForecastBlockProps> = ({ predictiveData, isLoading }) => {
  const getTrajectoryIcon = (trajectory: string) => {
    switch (trajectory) {
      case 'positive': return '📈';
      case 'negative': return '📉';
      case 'mixed': return '⚖️';
      case 'stable': return '➡️';
      default: return '❓';
    }
  };

  const getTrajectoryColor = (trajectory: string) => {
    switch (trajectory) {
      case 'positive': return 'text-green-600 bg-green-50';
      case 'negative': return 'text-red-600 bg-red-50';
      case 'mixed': return 'text-yellow-600 bg-yellow-50';
      case 'stable': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getTrajectoryTitle = (trajectory: string) => {
    switch (trajectory) {
      case 'positive': return 'Positive Trajectory';
      case 'negative': return 'Risk/Decline';
      case 'mixed': return 'Mixed/Unstable';
      case 'stable': return 'Stable';
      default: return 'Unknown';
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white  p-6 rounded-lg shadow-md">
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (!predictiveData) {
    return (
      <div className="bg-white  p-6 rounded-lg shadow-md">
        <div className="text-center py-8">
          <div className="text-4xl mb-4">🔮</div>
          <h3 className="text-lg font-medium mb-2">No Forecast Available</h3>
          <p className="text-gray-500 ">
            Continue chatting to build enough data for AI mood projections.
          </p>
        </div>
      </div>
    );
  }

  const { trajectory_analysis, forecast } = predictiveData;
  const trajectory = trajectory_analysis?.trajectory || 'unknown';
  const confidence = trajectory_analysis?.confidence || 0;

  return (
    <div className="bg-white  p-6 rounded-lg shadow-md">
      <div className="space-y-6">
        {/* Trajectory Overview */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="text-3xl">{getTrajectoryIcon(trajectory)}</div>
            <div>
              <h3 className="text-lg font-semibold">{getTrajectoryTitle(trajectory)}</h3>
              <p className="text-sm text-gray-500 ">
                Based on your recent patterns
              </p>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${getTrajectoryColor(trajectory)}`}>
            {Math.round(confidence * 100)}% confidence
          </div>
        </div>

        {/* Forecast Details */}
        {forecast?.forecast_text && (
          <div className="bg-gray-50  p-4 rounded-lg">
            <h4 className="font-medium mb-2">AI Forecast</h4>
            <p className="text-sm text-gray-700 ">
              {forecast.forecast_text}
            </p>
          </div>
        )}

        {/* Recommendations */}
        {forecast?.recommendations && forecast.recommendations.length > 0 && (
          <div>
            <h4 className="font-medium mb-3">Recommended Actions</h4>
            <div className="space-y-2">
              {forecast.recommendations.map((rec: string, index: number) => (
                <div key={index} className="flex items-start space-x-2">
                  <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <p className="text-sm text-gray-700 ">{rec}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Top Tags */}
        {forecast?.top_tags && forecast.top_tags.length > 0 && (
          <div>
            <h4 className="font-medium mb-3">Key Patterns Detected</h4>
            <div className="grid grid-cols-2 gap-3">
              {forecast.top_tags.slice(0, 4).map(([tag, count], index: number) => (
                <div key={index} className="bg-gray-50  p-3 rounded-lg">
                  <div className="text-sm font-medium text-gray-900 ">
                    {tag}
                  </div>
                  <div className="text-xs text-gray-500 ">
                    {count} mentions
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className="text-xs text-gray-500  bg-blue-50  p-3 rounded-lg">
          <strong>Note:</strong> This forecast is based on AI analysis of your communication patterns. 
          It's meant to provide insights, not replace professional mental health guidance.
        </div>
      </div>
    </div>
  );
};

export default ForecastBlock; 